import axios from "axios";
import API_URL from "../Config";

/**
 * One axios instance for the whole app, with token refresh handled centrally.
 *
 * Refreshes are de-duplicated: /api/refresh deletes the old refresh-token row,
 * so two concurrent refreshes would leave the second invalid and log the user
 * out.
 */
const client = axios.create({ baseURL: API_URL });

export const TOKEN_KEY = "access_token";
export const REFRESH_KEY = "refresh_token";

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// While a refresh is in flight every other 401 waits on this same promise,
// so the refresh endpoint is only ever called once per expiry.
let refreshPromise = null;

const runRefresh = async () => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error("No refresh token");

  const { data } = await axios.post(
    `${API_URL}/api/refresh`,
    { refresh_token: refreshToken },
    { headers: { "Content-Type": "application/json" } }
  );

  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_KEY, data.refresh_token);
  return data.access_token;
};

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only 401 means "token expired"; retrying on 400 would make genuine
    // validation errors look like auth failures.
    if (status !== 401 || original?._retried || original?.url?.includes("/api/refresh")) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const token = await refreshPromise;
      original.headers.Authorization = `Bearer ${token}`;
      return client(original);
    } catch (refreshError) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      // Let the app-level listener redirect to login.
      window.dispatchEvent(new Event("auth:expired"));
      return Promise.reject(refreshError);
    }
  }
);

/** Absolute URL for a server-relative path returned by the API. */
export const absoluteUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}/${String(path).replace(/^\/+/, "")}`;
};

export default client;
